/**
 * Banner API 服務
 * 使用 RTK Query 封裝 Banner 相關的 API 調用
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';
import {
  BannerStatus,
  IBanner,
  IBannerFormData,
  IBannerListParams,
  IPaginatedBannerResponse,
} from '@/types/banner';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * API 響應格式
 */
interface IApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Banner API Slice
 */
export const bannerApi = createApi({
  reducerPath: 'bannerApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Banner'],
  endpoints: (builder) => ({
    /**
     * 獲取 Banners 列表
     */
    getBanners: builder.query<
      IApiResponse<IPaginatedBannerResponse>,
      IBannerListParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_BANNERS,
        method: 'GET',
        params: params as Record<string, unknown>,
      }),
      providesTags: ['Banner'],
    }),

    /**
     * 獲取單個 Banner
     */
    getBannerById: builder.query<IApiResponse<IBanner>, string>({
      query: (id) => ({
        url: ConstantUtil.API_BANNER_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Banner', id }],
    }),

    /**
     * 刪除 Banner
     */
    deleteBanner: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_BANNER_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Banner'],
    }),

    /**
     * 更新 Banner 狀態
     */
    updateBannerStatus: builder.mutation<
      IApiResponse<IBanner>,
      { id: string; status: BannerStatus }
    >({
      query: ({ id, status }) => ({
        url: ConstantUtil.API_BANNER_UPDATE_STATUS(id),
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Banner', id },
        'Banner',
      ],
    }),

    /**
     * 新增 Banner
     */
    createBanner: builder.mutation<IApiResponse<IBanner>, IBannerFormData>({
      query: (formData) => ({
        url: ConstantUtil.API_BANNER_CREATE,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Banner'],
    }),

    /**
     * 更新 Banner
     */
    updateBanner: builder.mutation<
      IApiResponse<IBanner>,
      { id: string; formData: IBannerFormData }
    >({
      query: ({ id, formData }) => ({
        url: ConstantUtil.API_BANNER_UPDATE(id),
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Banner', id },
        'Banner',
      ],
    }),
  }),
});

export const {
  useGetBannersQuery,
  useGetBannerByIdQuery,
  useDeleteBannerMutation,
  useUpdateBannerStatusMutation,
  useCreateBannerMutation,
  useUpdateBannerMutation,
} = bannerApi;
